/**
 * Data hooks of the stat-block form (1.AC-2 deliverable 2). Reads: `docs.meta`, `types.list` and
 * `sheets.list` (key `['sheets', docId]`). Writes: `sheets.create` and `sheets.update`, through
 * `savePlan`, adopting the answer into the cache. `doc.changed` carries no sheet version, so the
 * page refetches the list on it and decides with `remoteChange`.
 */
import {
  type ActorSheetV1,
  ApiError,
  type DocMetaV1,
  type DocTypeSummaryV1,
  docsMeta,
  sheetsCreate,
  sheetsList,
  sheetsUpdate,
  typesList,
} from '@codex/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../../auth/client.js';
import { appEvents } from '../../editor-services/events.js';
import type { FormState } from '../model/draft.js';
import { savePlan } from '../model/save.js';

export type LoadFailure = 'missing' | 'failed';

/** A 404 is `missing`, anything else `failed`. */
export function failureOf(error: unknown): LoadFailure {
  return error instanceof ApiError && error.status === 404 ? 'missing' : 'failed';
}

export const metaKey = (docId: string) => ['meta', docId] as const;
export const typesKey = ['types'] as const;
export const sheetsKey = (docId: string) => ['sheets', docId] as const;

export interface SheetDoc {
  readonly doc: DocMetaV1 | undefined;
  readonly type: DocTypeSummaryV1 | undefined;
  /** A canon, live document whose type has `has_sheet`. */
  readonly editable: boolean;
  readonly loading: boolean;
  readonly failure: LoadFailure | null;
  readonly retry: () => void;
}

export function useSheetDoc(docId: string, enabled: boolean): SheetDoc {
  const meta = useQuery({
    queryKey: metaKey(docId),
    queryFn: () => api(docsMeta, { params: { id: docId } }),
    enabled,
    retry: false,
  });
  const types = useQuery({
    queryKey: typesKey,
    queryFn: async () => (await api(typesList)).items,
    enabled,
    retry: false,
  });
  const doc = meta.data;
  const type = doc ? types.data?.find((t) => t.id === doc.typeId) : undefined;
  const editable =
    doc !== undefined &&
    doc.library === 'canon' &&
    doc.deletedAt === null &&
    type?.capabilities.includes('has_sheet') === true;
  const error = meta.isError ? meta.error : types.isError ? types.error : null;
  return {
    doc,
    type,
    editable,
    loading: enabled && error === null && (doc === undefined || types.data === undefined),
    failure: error === null ? null : failureOf(error),
    retry: () => {
      void meta.refetch();
      void types.refetch();
    },
  };
}

/** The document's sheets, the primary first; refetched on `doc.changed` of the document. */
export function useSheets(docId: string, enabled: boolean) {
  const client = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    return appEvents.subscribeDocChanged(docId, () => {
      void client.invalidateQueries({ queryKey: sheetsKey(docId) });
    });
  }, [client, docId, enabled]);
  return useQuery({
    queryKey: sheetsKey(docId),
    queryFn: async () => (await api(sheetsList, { params: { id: docId } })).items,
    enabled,
    retry: false,
  });
}

/** Puts a saved sheet into the cached list (replacing its row or adding it), the primary first. */
export function withSheet(list: readonly ActorSheetV1[], sheet: ActorSheetV1): ActorSheetV1[] {
  const others = list.filter((row) => row.id !== sheet.id);
  const merged = [...others, sheet];
  return merged.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
}

export interface SaveInput {
  readonly state: FormState;
  readonly saved: ActorSheetV1 | null;
}

/** Runs `savePlan`; resolves the answer's sheet, or null when there was nothing to send. */
export function useSaveSheet(docId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ state, saved }: SaveInput): Promise<ActorSheetV1 | null> => {
      const plan = savePlan(state, saved);
      if (plan.kind === 'none') return null;
      if (plan.kind === 'create') {
        return api(sheetsCreate, { params: { id: docId }, body: plan.body });
      }
      return api(sheetsUpdate, {
        params: { id: docId, sheetId: (saved as ActorSheetV1).id },
        body: plan.body,
      });
    },
    onSuccess: (sheet) => {
      if (!sheet) return;
      client.setQueryData<ActorSheetV1[]>(sheetsKey(docId), (list) => withSheet(list ?? [], sheet));
      client.setQueryData(['sheet', sheet.id], sheet);
    },
  });
}
